import { useContext, useCallback } from 'react';
import { BeerContext } from '../BeerContext';
import { 
  SetSearchTermAction,
  SetTypeFilterAction,
  SetBreweryFilterAction,
  SetDrinkTypeFilterAction,
  SetAvailabilityFilterAction,
  AddTagFilterAction,
  RemoveTagFilterAction,
  ClearTagFiltersAction,
  ClearAllFiltersAction,
  SetShowOnlyFavoritesAction,
  SetShowOnlyAvailableAction,
  SetFilterByFavoritesAction
} from '../actions/filterActions';
import { FiltersState } from '../reducers/filtersReducer';

/**
 * Hook for accessing and manipulating filter state
 * Following Interface Segregation Principle by providing a focused API
 */
export function useFilters() {
  const context = useContext(BeerContext);
  
  if (context === null) {
    throw new Error('useFilters must be used within a BeerProvider');
  }
  
  const { state, dispatch } = context;
  
  // Filter state selectors
  const filters: FiltersState = state.filters;
  
  // Filter action creators
  const setSearchTerm = useCallback((searchTerm: string) => {
    dispatch(new SetSearchTermAction(searchTerm));
  }, [dispatch]);
  
  const setTypeFilter = useCallback((typeFilter: string) => {
    dispatch(new SetTypeFilterAction(typeFilter));
  }, [dispatch]);
  
  const setBreweryFilter = useCallback((breweryFilter: string) => {
    dispatch(new SetBreweryFilterAction(breweryFilter));
  }, [dispatch]);
  
  const setDrinkTypeFilter = useCallback((drinkTypeFilter: string) => {
    dispatch(new SetDrinkTypeFilterAction(drinkTypeFilter));
  }, [dispatch]);
  
  const setAvailabilityFilter = useCallback((availabilityFilter: string) => {
    dispatch(new SetAvailabilityFilterAction(availabilityFilter));
  }, [dispatch]);
  
  const addTagFilter = useCallback((tag: string) => {
    dispatch(new AddTagFilterAction(tag));
  }, [dispatch]);
  
  const removeTagFilter = useCallback((tag: string) => {
    dispatch(new RemoveTagFilterAction(tag));
  }, [dispatch]);
  
  const clearTagFilters = useCallback(() => {
    dispatch(new ClearTagFiltersAction());
  }, [dispatch]);
  
  const clearAllFilters = useCallback(() => {
    dispatch(new ClearAllFiltersAction());
  }, [dispatch]);
  
  const setShowOnlyFavorites = useCallback((showOnlyFavorites: boolean) => {
    dispatch(new SetShowOnlyFavoritesAction(showOnlyFavorites));
  }, [dispatch]);
  
  const setShowOnlyAvailable = useCallback((showOnlyAvailable: boolean) => {
    dispatch(new SetShowOnlyAvailableAction(showOnlyAvailable));
  }, [dispatch]);
  
  const setFilterByFavorites = useCallback((filterByFavorites: boolean) => {
    dispatch(new SetFilterByFavoritesAction(filterByFavorites));
  }, [dispatch]);
  
  return {
    // State
    filters,
    
    // Actions
    setSearchTerm,
    setTypeFilter,
    setBreweryFilter,
    setDrinkTypeFilter,
    setAvailabilityFilter,
    addTagFilter,
    removeTagFilter,
    clearTagFilters,
    clearAllFilters,
    setShowOnlyFavorites,
    setShowOnlyAvailable,
    setFilterByFavorites,
  };
}
